import { Button, Form, Modal, notification } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getInstructorById,
  getInstructors,
  updateInstructorById,
} from "store/slices/instructorsSlice";
import { closeModal } from "store/slices/modalsSlice";
import { getAllSchools } from "store/slices/schoolsSlice";
import InstructorForm from "./instructor-form";
import { IMAGE_URL } from "constants/ApiConstant";

const EditInstructorModal = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { instructor, loading } = useSelector((state) => state?.instructors);
  const { allSchools } = useSelector((state) => state?.schools);
  const open = isModalOpen && modalType === "edit_instructor";

  const { userData } = useSelector((state) => state?.auth);

  useEffect(() => {
    if (open && userData?._id) dispatch(getAllSchools());
  }, [dispatch, open, userData?._id]);

  useEffect(() => {
    if (modalProps?.instructorId && open) {
      dispatch(getInstructorById(modalProps?.instructorId));
    }
  }, [dispatch, modalProps?.instructorId, open]);

  useEffect(() => {
    if (instructor && open) {
      form?.setFieldsValue({
        ...instructor,
        cv: null,
        image: null,
        birthDate: instructor?.birthDate ? dayjs(instructor?.birthDate) : null,
      });

      if (instructor?.image) {
        form?.setFieldsValue({
          image: [
            {
              uid: "-1",
              name: "image.png",
              status: "done",
              url: `${IMAGE_URL}/${instructor?.image}`,
            },
          ],
        });
      }
      form?.setFieldsValue({ isFeatured: instructor?.isFeatured || false });

      if (instructor?.cv) {
        form?.setFieldsValue({
          cv: [
            {
              uid: "-1",
              name: "cv.pdf",
              status: "done",
              url: `${IMAGE_URL}/${instructor?.cv}`,
            },
          ],
        });
      }
    }
  }, [form, instructor, open]);

  const onSubmit = async (values) => {
    try {
      const formData = new FormData();
      const appendIfPresent = (key, value) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      };

      // Ajouter les champs textuels modifiés
      appendIfPresent("fullName", values?.fullName);
      appendIfPresent("email", values?.email);
      appendIfPresent("phoneNumber", values?.phoneNumber);
      appendIfPresent("wilaya", values?.wilaya);
      appendIfPresent("identityCardNumber", values?.identityCardNumber);
      appendIfPresent("schoolId", values?.schoolId);
      appendIfPresent("title", values?.title);
      appendIfPresent("speciality", values?.speciality);
      appendIfPresent("profile", values?.profile);
      appendIfPresent("shortDescription", values?.shortDescription);
      
      appendIfPresent("isFeatured", values?.isFeutured);
      console.log("isFeatured:", values?.isFeatured);
      // Formater la date de naissance au format YYYY-MM-DD
      if (values?.birthDate) {
        formData?.append(
          "birthDate",
          dayjs(values?.birthDate)?.format("YYYY-MM-DD")
        );
      }

      // Ajouter les fichiers si modifiés
      if (values?.image && values?.image[0]?.originFileObj) {
        formData?.append("instructorImg", values?.image[0]?.originFileObj);
      }

      if (values?.cv && values?.cv[0]?.originFileObj) {
        formData?.append("cv", values?.cv[0]?.originFileObj);
      }

      // N'envoyer le mot de passe que s'il est fourni
      if (values?.password) {
        formData?.append("password", values?.password);
        formData?.append("confirmPassword", values?.confirmPassword);
      }

      await dispatch(
        updateInstructorById({
          id: modalProps?.instructorId,
          data: formData,
        })
      )?.unwrap();

      // L'API de mise à jour ne renvoie qu'un message de statut, donc le store
      // n'est pas rafraîchi automatiquement : on recharge la liste pour voir
      // la nouvelle photo / les champs modifiés sans recharger la page.
      dispatch(getInstructors());
      if (modalProps?.instructorId) {
        dispatch(getInstructorById(modalProps?.instructorId));
      }

      notification?.success({
        message: t("instructors.crud.form.update.success"),
        description: t("instructors.crud.form.update.success.description"),
      });

      onClose();
    } catch (error) {
      notification?.error({
        message: t("instructors.crud.form.error.failed"),
        description:
          error?.message || t("instructors.crud.form.error.description"),
        duration: 5,
      });
    }
  };

  const onClose = () => {
    form?.resetFields();
    dispatch(closeModal());
  };

  return (
    <Modal
      title={
        <h4 style={{ textAlign: "center" }}>{t("instructors.crud.edit")}</h4>
      }
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("instructors.crud.form.button.cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          htmlType="submit"
          form="instructor_form"
          disabled={loading}
        >
          {t("instructors.crud.form.button.save")}
        </Button>,
      ]}
      centered
      styles={{ content: { overflowY: "auto", maxHeight: "75vh" } }}
    >
      <InstructorForm
        form={form}
        onSubmit={onSubmit}
        schools={allSchools}
        isEditing={true}
      />
    </Modal>
  );
};

export default EditInstructorModal;
